import { API_CONFIG } from './config';

export const requestRoomJson = async (path, options = {}) => {
  const baseUrl = API_CONFIG.roomApiBaseUrl?.replace(/\/$/, '');
  if (!baseUrl || baseUrl.includes('undefined') || !API_CONFIG.roomInstitutionReference) {
    throw new Error('Room booking is not configured. Please contact the reception.');
  }
  const url = `${baseUrl}${path}`;
  const response = await fetch(url, {
    ...options,
    headers: { accept: 'application/json', ...options.headers },
  });
  let payload;
  try {
    payload = JSON.parse(await response.text());
  } catch {
    throw new Error(`Room API returned a non-JSON response (${response.status}) from ${url}. Check the room API base URL and server routing.`);
  }
  if (!response.ok || payload?.status !== 1) {
    throw new Error(payload?.error?.message || 'Unable to complete the room request. Please try again.');
  }
  return payload.data;
};

export const postRoomJson = (path, body) => requestRoomJson(path, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

// The current website reserves one room. Accept the reference containers used by
// the existing integration, but never truncate a string or silently drop rooms.
export const extractRoomAllocationReference = (data) => {
  const collect = (value) => {
    if (typeof value === 'string') return value.trim() ? [value.trim()] : [];
    if (Array.isArray(value)) return value.flatMap(collect);
    if (!value || typeof value !== 'object') return [];
    return collect(value.roomAllocationReference ?? value.reservationReferences ?? value.roomAllocations);
  };
  const references = [...new Set(collect(data))];
  if (references.length !== 1) {
    throw new Error('Unable to identify the room reservation. Please select the room type again.');
  }
  return references[0];
};

export const freezeRoomConfiguration = async ({ room, checkInDate, checkOutDate, arrivalTime, numberOfPerson }) => {
  const configurationId = Number(room.floorRoomTypeMapId);
  const guests = Number(numberOfPerson);
  if (!Number.isInteger(configurationId) || configurationId <= 0) {
    throw new Error('This room type cannot be reserved right now. Please choose another.');
  }
  if (!Number.isInteger(guests) || guests < 1 || !Number.isFinite(Number(room.capacity)) || guests > Number(room.capacity)) {
    throw new Error('Please choose a room type that can accommodate all guests.');
  }
  if (!Number.isFinite(checkInDate) || !Number.isFinite(checkOutDate) || checkOutDate <= checkInDate) {
    throw new Error('Please select valid booking dates.');
  }
  const data = await postRoomJson('/freeze-room', {
    roomConfigurations: [{ floorRoomTypeMapId: configurationId, numberOfRooms: 1 }],
    institutionReference: API_CONFIG.roomInstitutionReference,
    checkInDate,
    checkOutDate,
    arrivalTime,
    numberOfPerson: guests,
  });
  return {
    roomAllocationReference: extractRoomAllocationReference(data),
    numberOfPerson: guests,
    checkInTimestamp: checkInDate,
    checkOutTimestamp: checkOutDate,
    arrivalTime,
  };
};

export const reserveRoom = ({ form, paymentChannelId, roomAllocationReference }) => {
  const reference = extractRoomAllocationReference(roomAllocationReference);
  return postRoomJson('/reserve-room', {
    phone: form.phone.replace(/\D/g, ''),
    address: form.address.trim(),
    devoteeNameDup: null,
    devoteeName: form.name.trim(),
    emailAddress: form.email.trim(),
    paymentChannelId: String(paymentChannelId),
    institutionReference: API_CONFIG.roomInstitutionReference,
    parentAllocationReference: null,
    reservationReferences: [{ roomAllocationReference: reference, isMasterRoomAllocation: true }],
  });
};
